import React, { useState } from 'react';
import { signIn, confirmSignIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { TextField, Button, Alert, Link, Stack } from '@mui/material';
import AuthLayout from './components/AuthLayout';
import './aws-config';

const Login = ({ onLogin, switchToRegister }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState(1); // 1=enter email, 2=enter code+new password
  const [forgotSuccess, setForgotSuccess] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signIn({ username: email, password });
      const { isSignedIn, nextStep } = result;
      if (isSignedIn) {
        onLogin({ email });
      } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
      }
    } catch (error) {
      setError(error.message || 'Login failed');
    }
    setLoading(false);
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { isSignedIn } = await confirmSignIn({ challengeResponse: newPassword });
      if (isSignedIn) {
        onLogin({ email });
      }
    } catch (error) {
      setError(error.message || 'Password change failed');
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await resetPassword({ username: email });
      setForgotStep(2);
      setForgotSuccess('Verification code sent to your email.');
    } catch (error) {
      setError(error.message || 'Failed to send reset code');
    }
    setLoading(false);
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await confirmResetPassword({ username: email, confirmationCode: resetCode, newPassword });
      setForgotSuccess('Password reset successful! You can now login.');
      setTimeout(() => {
        setForgotMode(false);
        setForgotStep(1);
        setForgotSuccess('');
        setResetCode('');
        setNewPassword('');
      }, 2000);
    } catch (error) {
      setError(error.message || 'Password reset failed');
    }
    setLoading(false);
  };

  const linkBtn = (label, onClick) => (
    <Link component="button" type="button" underline="hover" onClick={onClick} sx={{ color: 'var(--link-on-dark)', fontWeight: 600 }}>{label}</Link>
  );

  if (needsNewPassword) {
    return (
      <AuthLayout title="Set a new password" subtitle="Your temporary password must be changed before you continue.">
        <form onSubmit={handleNewPassword}>
          <Stack spacing={2}>
            <TextField fullWidth type="password" label="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
              helperText="Minimum 8 characters, with uppercase, lowercase and a number." />
            {error && <Alert severity="error">{error}</Alert>}
            <Button type="submit" variant="contained" size="large" disabled={loading}>{loading ? 'Setting password…' : 'Set password & login'}</Button>
          </Stack>
        </form>
      </AuthLayout>
    );
  }

  if (forgotMode) {
    return (
      <AuthLayout title="Reset password" footer={linkBtn('Back to login', () => { setForgotMode(false); setError(''); setForgotStep(1); })}>
        {forgotStep === 1 ? (
          <form onSubmit={handleForgotPassword}>
            <Stack spacing={2}>
              <TextField fullWidth type="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required helperText="We will email you a verification code." />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large" disabled={loading}>{loading ? 'Sending…' : 'Send reset code'}</Button>
            </Stack>
          </form>
        ) : (
          <form onSubmit={handleConfirmReset}>
            <Stack spacing={2}>
              {forgotSuccess && <Alert severity="success">{forgotSuccess}</Alert>}
              <TextField fullWidth label="Verification code" value={resetCode} onChange={(e) => setResetCode(e.target.value)} required placeholder="6-digit code" />
              <TextField fullWidth type="password" label="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                helperText="Minimum 8 characters, with uppercase, lowercase and a number." />
              {error && <Alert severity="error">{error}</Alert>}
              <Button type="submit" variant="contained" size="large" disabled={loading}>{loading ? 'Resetting…' : 'Reset password'}</Button>
            </Stack>
          </form>
        )}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Log in to analyse your SQL Server workloads."
      footer={<>Need an account? {linkBtn('Request access', switchToRegister)}</>}
    >
      <form onSubmit={handleLogin}>
        <Stack spacing={2}>
          <TextField fullWidth type="email" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          <TextField fullWidth type="password" label="Password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" size="large" disabled={loading}>{loading ? 'Logging in…' : 'Login'}</Button>
          <Link component="button" type="button" underline="hover" onClick={() => { setForgotMode(true); setError(''); }} sx={{ alignSelf: 'center', fontSize: 14 }}>
            Forgot password?
          </Link>
        </Stack>
      </form>
    </AuthLayout>
  );
};

export default Login;
